import { apiRequest } from './api';

export interface SupportTicket {
  id: string;
  user_id: string;
  club_id: string | null;
  subject: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  // Single most-recent admin reply. Surfaced inline so the requester
  // can read it without opening a thread; expanded to a join table
  // when/if we add multi-message threads.
  admin_response: string | null;
  admin_response_at: string | null;
  responded_by_id: string | null;
  responded_by?: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  user?: {
    id: string;
    email: string;
    full_name: string | null;
  };
  club?: {
    id: string;
    name: string;
  } | null;
}

export interface CreateTicketPayload {
  subject: string;
  message: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  club_id?: string;
}

export interface SupportStats {
  total: number;
  recent_7_days: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
}

export async function getSupportTickets(params?: { status?: string; club_id?: string }): Promise<SupportTicket[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append('status', params.status);
  if (params?.club_id) searchParams.append('club_id', params.club_id);
  const query = searchParams.toString();
  return apiRequest<SupportTicket[]>('/support' + (query ? '?' + query : ''));
}

export async function createSupportTicket(payload: CreateTicketPayload): Promise<SupportTicket> {
  return apiRequest<SupportTicket>('/support', { method: 'POST', data: payload });
}

export async function getSupportTicket(id: string): Promise<SupportTicket> {
  return apiRequest<SupportTicket>('/support/' + id);
}

export async function updateTicketStatus(id: string, status: 'open' | 'in_progress' | 'resolved' | 'closed'): Promise<SupportTicket> {
  return apiRequest<SupportTicket>('/support/' + id, { method: 'PATCH', data: { status } });
}

export async function deleteSupportTicket(id: string): Promise<{ success: boolean; message: string }> {
  return apiRequest('/support/' + id, { method: 'DELETE' });
}

/**
 * Admin reply. The backend stores the response on the ticket, emails
 * the original reporter, and transitions the status (default "resolved";
 * pass `status` to override). Returns the updated ticket.
 */
export async function replyToSupportTicket(
  id: string,
  payload: { response: string; status?: 'open' | 'in_progress' | 'resolved' | 'closed' }
): Promise<SupportTicket> {
  return apiRequest<SupportTicket>('/support/' + id + '/reply', {
    method: 'POST',
    data: payload,
  });
}

export async function getSupportStats(): Promise<SupportStats> {
  return apiRequest<SupportStats>('/support/stats/summary');
}

// Aliases for backwards compatibility
export const getTickets = getSupportTickets;
export const createTicket = createSupportTicket;